import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../components/Icon';
import { LoadingBlock, TopBar } from '../components/ui';
import SwipeDeck from './discover/SwipeDeck';
import BrowseGrid from './discover/BrowseGrid';
import { loadSwipes, recordSwipe, saveSwipes } from '../lib/swipes';

const MODE_KEY = 'date9.discoverMode';

const FILTERS = [
  { id: 'all', label: 'Everyone' },
  { id: 'liked', label: 'Liked' },
  { id: 'online', label: 'Active now' },
  { id: 'people', label: 'People' },
  { id: 'ai', label: 'AI' },
];

export default function DiscoverScreen({ me, people, loading, onRefresh, onOpenProfile, onOpenChat, showToast }) {
  const [mode, setMode] = useState(() => window.localStorage.getItem(MODE_KEY) || 'swipe');
  const [swipes, setSwipes] = useState(() => loadSwipes(me.id));
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    window.localStorage.setItem(MODE_KEY, mode);
  }, [mode]);

  const others = useMemo(() => people.filter((p) => p._id !== me.id), [people, me.id]);

  const deckQueue = useMemo(() => {
    const decided = new Set([...swipes.liked, ...swipes.passed]);
    return others.filter((p) => !decided.has(p._id));
  }, [others, swipes]);

  const browseList = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return others.filter((person) => {
      if (filter === 'liked' && !swipes.liked.includes(person._id)) return false;
      if (filter === 'ai' && !person.isBot) return false;
      if (filter === 'people' && person.isBot) return false;
      if (filter === 'online' && !(person.lastSeen && Date.now() - new Date(person.lastSeen).getTime() < 120_000))
        return false;
      if (!needle) return true;
      return (
        person.username?.toLowerCase().includes(needle) || person.bio?.toLowerCase().includes(needle)
      );
    });
  }, [others, query, filter, swipes.liked]);

  const persist = (next) => {
    setSwipes(next);
    saveSwipes(me.id, next);
  };

  const handleDecide = (person, decision) => {
    persist(recordSwipe(swipes, person._id, decision));
    if (decision === 'like') {
      showToast(`You liked ${person.username} — say hello!`, 'success');
    }
  };

  const resetDeck = () => {
    persist({ liked: swipes.liked, passed: [] });
  };

  return (
    <div className="screen">
      <TopBar
        right={
          <button
            type="button"
            className="icon-btn icon-btn-plain"
            onClick={onRefresh}
            aria-label="Refresh people"
            disabled={loading}
          >
            <Icon name="refresh" size={18} />
          </button>
        }
      >
        <div className="topbar-title">
          <div className="brand-mark">
            <span className="brand-mark-icon">
              <Icon name="flame" size={18} />
            </span>
            Date9
          </div>
        </div>
      </TopBar>

      <div className="screen-body">
        <div className="discover-toolbar">
          <div className="segmented" role="tablist" aria-label="Discover mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'swipe'}
              className={`segmented-item${mode === 'swipe' ? ' active' : ''}`}
              onClick={() => setMode('swipe')}
            >
              <Icon name="layers" size={16} />
              Swipe
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'browse'}
              className={`segmented-item${mode === 'browse' ? ' active' : ''}`}
              onClick={() => setMode('browse')}
            >
              <Icon name="grid" size={16} />
              Browse
            </button>
          </div>

          {mode === 'browse' && (
            <>
              <div className="field-input-wrap">
                <span className="field-icon">
                  <Icon name="search" size={17} />
                </span>
                <input
                  className="input input-with-icon"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or bio"
                  aria-label="Search people"
                />
              </div>
              <div className="chip-row">
                {FILTERS.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    className={`chip${filter === option.id ? ' active' : ''}`}
                    onClick={() => setFilter(option.id)}
                    aria-pressed={filter === option.id}
                  >
                    {option.label}
                    {option.id === 'liked' && swipes.liked.length > 0 && ` · ${swipes.liked.length}`}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {loading ? (
          <LoadingBlock text="Finding people near you…" />
        ) : mode === 'swipe' ? (
          <SwipeDeck
            people={deckQueue}
            onDecide={handleDecide}
            onOpenProfile={onOpenProfile}
            onReset={resetDeck}
            onBrowse={() => setMode('browse')}
          />
        ) : (
          <BrowseGrid
            people={browseList}
            onOpenProfile={onOpenProfile}
            onOpenChat={onOpenChat}
            emptyText={
              query
                ? `No one matches “${query}”. Try a different search.`
                : 'No profiles match this filter yet.'
            }
          />
        )}
      </div>
    </div>
  );
}
